import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Github, Link2, Unlink, ExternalLink, Loader2 } from 'lucide-react';
import { githubAuthApi } from '@/api/github';
import { formatDistanceToNow } from 'date-fns';

export function GitHubSettings() {
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery({
    queryKey: ['github-status'],
    queryFn: () => githubAuthApi.getStatus(),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => githubAuthApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['github-status'] });
    },
  });

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const response = await githubAuthApi.getConnectUrl();
      // Store state for CSRF protection
      sessionStorage.setItem('github_oauth_state', response.state);
      // Redirect to GitHub OAuth
      window.location.href = response.authorizationUrl;
    } catch (error) {
      console.error('Failed to get GitHub connect URL:', error);
      setIsConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          <span className="text-gray-500">Loading GitHub status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Github className="h-6 w-6 text-gray-900 dark:text-white" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          GitHub Integration
        </h2>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Connect your GitHub account to push code, create repositories, and keep your projects in sync.
      </p>

      {status?.connected ? (
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {status.avatarUrl ? (
                  <img src={status.avatarUrl} alt={status.username} className="h-full w-full object-cover" />
                ) : (
                  <Github className="h-5 w-5" />
                )}
              </div>
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-white">
                @{status.username}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Connected {status.connectedAt && formatDistanceToNow(new Date(status.connectedAt), { addSuffix: true })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`https://github.com/${status.username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          <button
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            {disconnectMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Unlink className="h-4 w-4" />
            )}
            Disconnect GitHub
          </button>
        </div>
      ) : (
        <button
          onClick={handleConnect}
          disabled={isConnecting}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
        >
          {isConnecting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}
          Connect GitHub
        </button>
      )}
    </div>
  );
}
